import { useCallback, useEffect, useRef, useState } from "react";
import { requestDocumentFillerTicket } from "../services/documentFillerService";

const WS_URL = import.meta.env.VITE_AI_DOCUMENT_FILLER_WS_URL;

/**
 * Opens a ticket-authenticated WebSocket for document-filler job events.
 *
 * @param {{ onMessage?: (payload: object) => void, enabled?: boolean }} options
 * @returns {{ status: "disconnected" | "connecting" | "connected", reconnect: () => void }}
 */
export function useDocumentFillerSocket({ onMessage, enabled = false }) {
  const [status, setStatus] = useState("disconnected");
  const [connectionVersion, setConnectionVersion] = useState(0);
  const socketRef = useRef(null);
  const onMessageRef = useRef(onMessage);

  onMessageRef.current = onMessage;

  const reconnect = useCallback(() => {
    setConnectionVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus("disconnected");
      return undefined;
    }

    let active = true;
    let socket = null;

    const connect = async () => {
      setStatus("connecting");
      const ticketResult = await requestDocumentFillerTicket();

      if (!active) return;

      if (!ticketResult.ok || !WS_URL) {
        setStatus("disconnected");
        return;
      }

      try {
        socket = new WebSocket(
          `${WS_URL}?ticket=${encodeURIComponent(ticketResult.ticket)}`
        );
      } catch (error) {
        console.error("useDocumentFillerSocket: failed to connect", error);
        setStatus("disconnected");
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        if (active && socketRef.current === socket) {
          setStatus("connected");
        }
      };

      socket.onmessage = (event) => {
        if (!active || socketRef.current !== socket) return;

        try {
          onMessageRef.current?.(JSON.parse(event.data));
        } catch (error) {
          console.error("useDocumentFillerSocket: invalid message payload", error);
        }
      };

      socket.onclose = () => {
        if (active && socketRef.current === socket) {
          socketRef.current = null;
          setStatus("disconnected");
        }
      };
    };

    void connect();

    return () => {
      active = false;
      if (socketRef.current === socket) socketRef.current = null;
      socket?.close();
    };
  }, [connectionVersion, enabled]);

  return { status, reconnect };
}

export default useDocumentFillerSocket;
